import React from "react";
import RequestEngine from "../../core/RequestEngine";
import SocketEngine from "../../core/SocketEngine";
import strings from "../../core/translate";
import { onNotificationArrived } from "../../core/pushNotifications";

const PATH = "merchants-users/notifications";

/**
 * Bell + unread badge in the top bar.
 *
 * The count is always re-read from the API rather than incremented locally:
 * the same notification can arrive over the socket and as a web push, and a
 * second tab may have already read it, so a local +1 would drift. Every
 * signal simply means "something changed, re-check".
 */
export default class NotificationBell extends React.Component {
  constructor(props) {
    super(props);
    this.state = { count: 0, items: [], open: false, loading: false };
    this.rootRef = React.createRef();
  }

  componentDidMount() {
    this.refreshCount();

    // a booking notification reaches the panel as notification:new; the
    // coarse list events fire for the same underlying activity
    this.onRealtime = () => this.refreshCount();
    SocketEngine.on("notification:new", this.onRealtime);
    SocketEngine.on("booking:updated", this.onRealtime);
    SocketEngine.on("work_order:updated", this.onRealtime);

    // and the same event arriving as a web push while the tab is open
    this.offPush = onNotificationArrived(() => this.refreshCount());

    document.addEventListener("mousedown", this.handleOutside);
  }

  componentWillUnmount() {
    SocketEngine.off("notification:new", this.onRealtime);
    SocketEngine.off("booking:updated", this.onRealtime);
    SocketEngine.off("work_order:updated", this.onRealtime);
    if (this.offPush) this.offPush();
    document.removeEventListener("mousedown", this.handleOutside);
  }

  handleOutside = (event) => {
    if (
      this.state.open &&
      this.rootRef.current &&
      !this.rootRef.current.contains(event.target)
    ) {
      this.setState({ open: false });
    }
  };

  engine() {
    // built per call: RequestEngine snapshots the session token at
    // construction, so a long-lived instance can hold a stale one
    return new RequestEngine();
  }

  refreshCount = async () => {
    try {
      const response = await this.engine().getItem(`${PATH}/unread-count`);
      if (response && response.data) {
        this.setState({ count: response.data.count || 0 });
      }
    } catch (err) {
      // the bell is decoration if this fails — never break the page
    }
  };

  loadItems = async () => {
    this.setState({ loading: true });
    try {
      const response = await this.engine().getItem(`${PATH}?limit=20`);
      if (response && Array.isArray(response.data)) {
        this.setState({ items: response.data });
      }
    } catch (err) {
      // leave whatever was already listed
    }
    this.setState({ loading: false });
  };

  toggle = () => {
    const open = !this.state.open;
    this.setState({ open });
    if (open) this.loadItems();
  };

  markAllRead = async () => {
    try {
      await this.engine().patchItem(`${PATH}/read-all`);
      this.setState((prev) => ({
        count: 0,
        items: prev.items.map((it) => ({ ...it, read: true })),
      }));
    } catch (err) {
      // no-op
    }
  };

  clearAll = async () => {
    if (!window.confirm(strings.confirm_clear_notifications)) return;
    try {
      await this.engine().deletePath(PATH);
      this.setState({ items: [], count: 0 });
    } catch (err) {
      // no-op
    }
  };

  // The row itself is clickable, so this click must not travel up: deleting
  // an item should not first mark it read.
  removeItem = async (event, item) => {
    event.stopPropagation();
    try {
      await this.engine().deleteItem(PATH, item.id);
      this.setState((prev) => ({
        items: prev.items.filter((it) => it.id !== item.id),
        count: item.read ? prev.count : Math.max(0, prev.count - 1),
      }));
    } catch (err) {
      // leave the row alone — the next refresh re-reads the real count
    }
  };

  handleOpenItem = async (item) => {
    if (!item.read) {
      try {
        await this.engine().patchItem(`${PATH}/${item.id}/read`);
        this.setState((prev) => ({
          count: Math.max(0, prev.count - 1),
          items: prev.items.map((it) =>
            it.id === item.id ? { ...it, read: true } : it
          ),
        }));
      } catch (err) {
        // no-op
      }
    }
  };

  renderItem = (item) => (
    <li
      key={item.id}
      className={`topnav-bell__item${item.read ? "" : " is-unread"}`}
      onClick={() => this.handleOpenItem(item)}
    >
      <span className="topnav-bell__item-title">{item.title}</span>
      <span className="topnav-bell__item-body">{item.body}</span>
      <span className="topnav-bell__item-time">
        {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
      </span>
      <button
        type="button"
        className="topnav-bell__item-delete"
        title={strings.delete_notification}
        aria-label={strings.delete_notification}
        onClick={(event) => this.removeItem(event, item)}
      >
        <i className="nc-icon nc-simple-remove" />
      </button>
    </li>
  );

  render() {
    const { count, items, open, loading } = this.state;

    return (
      <div className="topnav-bell" ref={this.rootRef}>
        <button
          type="button"
          className="topnav__action topnav-bell__button"
          title={strings.notifications}
          aria-label={strings.notifications}
          onClick={this.toggle}
        >
          <i className="nc-icon nc-bell-55" />
          {count > 0 && (
            <span className="topnav-bell__badge">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>

        {open && (
          <div className="topnav-bell__panel">
            <div className="topnav-bell__head">
              <span>{strings.notifications}</span>
              <span className="topnav-bell__head-actions">
                {count > 0 && (
                  <button type="button" onClick={this.markAllRead}>
                    {strings.mark_all_read}
                  </button>
                )}
                {items.length > 0 && (
                  <button
                    type="button"
                    className="topnav-bell__clear"
                    title={strings.clear_notifications}
                    onClick={this.clearAll}
                  >
                    {strings.clear_all}
                  </button>
                )}
              </span>
            </div>
            {loading && items.length === 0 ? (
              <p className="topnav-bell__empty">{strings.loading}</p>
            ) : items.length === 0 ? (
              <p className="topnav-bell__empty">{strings.nodata}</p>
            ) : (
              <ul className="topnav-bell__list">{items.map(this.renderItem)}</ul>
            )}
          </div>
        )}
      </div>
    );
  }
}
